import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';

import {
  AgentDto,
  ToolDto,
  apiService,
  useCompanyControllerGetCompanyQuery,
} from '@shared/services/apiService/apiService';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import { AgentListProps, SortingAgentColumns } from '@shared/types/admin/agent';
import {
  formatTranscriberLabel,
  resolveTranscriberProviderDisplayName,
} from '@shared/utils/transcriberDisplay';

import { useStyles } from '../styles';
import classNames from 'classnames';

import { ReactComponent as AgentIcon } from '@shared/assets/images/agent/agent.svg';
import { IconActionPlay } from '@shared/assets/images/svgs';

import { ReactComponent as CallIcon } from '@shared/assets/images/agent/call-icon.svg';
import { ReactComponent as CampaignIcon } from '@shared/assets/images/agent/campaign-icon.svg';
import { ReactComponent as DeleteAgentIcon } from '@shared/assets/images/agent/delete-agent-icon.svg';
import { ReactComponent as EditAgentIcon } from '@shared/assets/images/agent/edit-icon.svg';
import { ManageAgent } from './ManageAgent';
import { TestingAgentPopup } from '@widgets/Popup/TestingAgentPopup/TestingAgentPopup';
import { StyledErrorText } from '@shared/components/errorText';
import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { useFormContext } from 'react-hook-form';
import { accentOptions, DEFAULT_NO_ROLE_AGENT_PROFILE } from './ManageAgent/constants';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { isEqual } from 'lodash';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import AgentAdvancedSettingsModal from './AgentAdvancedSettingsModal';
import { CreateSquadModal } from './CreateSquadModal';
import { useSquadManagement } from '@features/Admin/Agent/hooks/useSquadManagement';
import { useAgentOperations } from '@features/Admin/Agent/hooks/useAgentOperations';
import { useEditAgentTools } from '@features/Admin/Agent/hooks/useEditAgentTools';
import {
  SQUAD_OPERATIONS,
  SQUAD_VALIDATION,
} from '@features/Admin/Agent/constants/squad.constants';
import { LlmCompositeCell } from './AgentList/cells/LlmCompositeCell';
import type { LlmModelDto } from '@shared/services/apiService/apiService';
import { shallowEqual } from 'react-redux';
import { resolveModelTitle, resolveProviderTitle } from './AgentList/helpers/llmDisplay';
import { SortDirection, sortAgentsByColumn } from './AgentList/helpers/agentSorting';
import AgentAmbienceSettingsModal from './AgentAmbienceSettingsModal';
import { playVoicePreviewFromUserGesture } from './playVoicePreview';

export const AgentList: React.FC<AgentListProps> = ({
  setLoading,
  onRefresh,
  onAgentCreated,
  agentList,
  isSquad,
  campaignData,
}: AgentListProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const {
    formState: { errors: formStateErrors },
    setValue,
    getValues,
  } = useFormContext<CreateCampaignFormValue>();

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.authenticatedUser);

  // Local state must be declared before using in hooks
  const [agents, setAgents] = useState<AgentDto[]>(agentList);
  const [sortConfig, setSortConfig] = useState<{
    key: SortingAgentColumns | null;
    direction: SortDirection;
  }>({
    key: null,
    direction: 'asc',
  });

  const [showDialog, setShowDialog] = useState(false);
  const [showTestAgent, setShowTestAgent] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showAgentAmbienceSettings, setShowAgentAmbienceSettings] = useState(false);

  // LLM Provider/Model cells are extracted components to keep hooks/logic isolated

  const [deleteId, setDeleteId] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [showManageAgent, setShowManageAgent] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentDto | null>(null);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  const [copiedAgentId, setCopiedAgentId] = useState<string | null>(null);
  const [newlyAddedAgentId, setNewlyAddedAgentId] = useState<string | null>(null);
  const [deletingAgentId, setDeletingAgentId] = useState<string | null>(null);
  const [pendingNewAgentAnimation, setPendingNewAgentAnimation] = useState(false);
  const seenAgentIdsRef = useRef<Set<string>>(new Set((agentList || []).map((a) => a.id)));
  const newAgentAnimationTimeoutRef = useRef<number | null>(null);

  // Fetch campaign data to get tools and squad info
  const { campaignId } = useParams();
  const newAgentAnimationStorageKey = `agent-list-new-agent:${campaignId || 'global'}`;

  const squadId = campaignData?.squad?.id || null;
  const companyId = currentUser?.companyId ?? '';

  const { data: company } = useCompanyControllerGetCompanyQuery(
    { id: companyId },
    { skip: !companyId, refetchOnMountOrArgChange: true },
  );
  const voicesData = company?.voices;
  const transcriberData = company?.transcribers;
  const llmProviders = company?.llmProviders;

  const providerIdsNeedingModels = useMemo(() => {
    const ids = new Set<string>();

    for (const row of agents) {
      const provider = row.llmProvider || llmProviders?.find((p) => p.id === row.llmProviderId);
      const allowsCustom = provider?.allowsCustomModels;
      const providerId = provider?.id || row.llmProviderId;

      const shouldFetchModels =
        !row.llmCustomModelKey && !row.llmModel?.title && !!providerId && allowsCustom === false;

      if (shouldFetchModels && providerId) ids.add(providerId);
    }

    return Array.from(ids);
  }, [agents, llmProviders]);

  useEffect(() => {
    for (const id of providerIdsNeedingModels) {
      dispatch(
        apiService.util.prefetch('llmModelListControllerModelList', { id }, { ifOlderThan: 60 }),
      );
    }
  }, [dispatch, providerIdsNeedingModels]);

  const providerModelsByProviderId = useAppSelector((state) => {
    const result: Record<string, LlmModelDto[] | undefined> = {};

    for (const id of providerIdsNeedingModels) {
      result[id] = apiService.endpoints.llmModelListControllerModelList.select({ id })(state).data;
    }

    return result;
  }, shallowEqual);

  // Use squad management hook
  const squadManagement = useSquadManagement(campaignId, squadId, agentList, onRefresh);

  // Use agent operations hook
  const agentOperations = useAgentOperations(campaignId, squadId, agents, onRefresh);

  const { onSubmit: saveAgentTools, formMethods: agentToolsFormMethods } = useEditAgentTools({
    campaignId: campaignId || '',
    squadId,
    agent: selectedAgent,
    onSuccess: () => {
      onRefresh();
      handleCloseAdvancedSettings();
    },
  });

  const hasSquad = !!squadId;
  const hasSingleAgent = agents.length === 1 && !hasSquad;

  useEffect(() => {
    const sortedAgents = [...(agentList || [])].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });

    const incomingIds = new Set(sortedAgents.map((a) => a.id));
    const shouldAnimateFromStorage = Boolean(sessionStorage.getItem(newAgentAnimationStorageKey));
    const newlyAddedAgent =
      pendingNewAgentAnimation || shouldAnimateFromStorage
        ? (sortedAgents[0] ?? null)
        : (sortedAgents.find((a) => !seenAgentIdsRef.current.has(a.id)) ?? null);

    if (newlyAddedAgent) {
      setNewlyAddedAgentId(newlyAddedAgent.id);
      setPendingNewAgentAnimation(false);
      sessionStorage.removeItem(newAgentAnimationStorageKey);
      if (newAgentAnimationTimeoutRef.current) {
        window.clearTimeout(newAgentAnimationTimeoutRef.current);
      }
      newAgentAnimationTimeoutRef.current = window.setTimeout(() => {
        setNewlyAddedAgentId(null);
      }, 2200);
    }
    seenAgentIdsRef.current = incomingIds;

    setAgents(sortedAgents);

    const currentAgents = getValues('agents');

    if (!isEqual(currentAgents, sortedAgents)) {
      setValue('agents', sortedAgents);
    }
  }, [agentList, getValues, newAgentAnimationStorageKey, pendingNewAgentAnimation, setValue]);

  useEffect(() => {
    return () => {
      if (newAgentAnimationTimeoutRef.current) {
        window.clearTimeout(newAgentAnimationTimeoutRef.current);
      }
    };
  }, []);

  const getTranscriber = (agent: AgentDto) => {
    const transcriberId = agent.transcriberId || agent.transcriber?.id;
    if (transcriberId && transcriberData) {
      const fromCompany = transcriberData.find((t) => t.id === transcriberId);
      if (fromCompany) return fromCompany;
    }
    if (agent.transcriber?.provider && agent.transcriber?.language) {
      return agent.transcriber;
    }
    return null;
  };

  const getVoiceName = (agent: AgentDto): string => {
    if (agent.voice?.name) {
      return agent.voice.name;
    }
    if (agent.voiceId && voicesData) {
      const voice = voicesData.find((v) => v.id === agent.voiceId);
      return voice?.name || 'N/A';
    }
    return 'N/A';
  };

  const getVoiceGender = (agent: AgentDto): string => {
    if (agent.voice?.gender) {
      return agent.voice.gender.charAt(0).toUpperCase() + agent.voice.gender.slice(1);
    }
    if (agent.voiceId && voicesData) {
      const voice = voicesData.find((v) => v.id === agent.voiceId);
      if (voice?.gender) {
        return voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1);
      }
    }
    return '';
  };

  const getVoicePreviewUrl = (agent: AgentDto): string | null => {
    if (agent.voice?.previewUrl) {
      return agent.voice.previewUrl;
    }
    if (agent.voiceId && voicesData) {
      const voice = voicesData.find((v) => v.id === agent.voiceId);
      return voice?.previewUrl || null;
    }
    return null;
  };

  const getLanguageValue = (agent: AgentDto): string => {
    return (
      accentOptions.find((option) => option.key === agent.languages?.[0])?.label ||
      agent.languages?.[0] ||
      ''
    );
  };

  const getLlmProviderValue = (agent: AgentDto): string => {
    const providerTitle = resolveProviderTitle(agent, llmProviders, t);

    if (providerTitle === t('adminMainPage.agents.unknownProvider')) {
      return '';
    }

    return providerTitle;
  };

  const getLlmModelValue = (agent: AgentDto): string => {
    const provider = agent.llmProvider || llmProviders?.find((p) => p.id === agent.llmProviderId);
    const providerId = provider?.id || agent.llmProviderId;
    const providerModels = providerId ? providerModelsByProviderId?.[providerId] : undefined;
    const modelTitle = resolveModelTitle(agent, provider ?? null, providerModels, t);

    if (modelTitle === t('adminMainPage.agents.unknownModel')) {
      return '';
    }

    return modelTitle;
  };

  const getSortValue = (agent: AgentDto, key: SortingAgentColumns): string => {
    switch (key) {
      case 'mission.humanName':
      case 'name':
        return agent.name;
      case 'profile':
        return agent.profile?.name || DEFAULT_NO_ROLE_AGENT_PROFILE.name;
      case 'voice.gender':
        return getVoiceGender(agent);
      case 'voice.name':
        return getVoiceName(agent);
      case 'voice.accent':
        return getLanguageValue(agent);
      case 'transcriber.provider': {
        const transcriber = getTranscriber(agent);
        return transcriber ? resolveTranscriberProviderDisplayName(transcriber) : '';
      }
      case 'transcriber.language': {
        const transcriber = getTranscriber(agent);
        return transcriber?.language || '';
      }
      case 'llm.provider':
        return getLlmProviderValue(agent);
      case 'llm.model':
        return getLlmModelValue(agent);
      default:
        return '';
    }
  };

  const getActionTooltip = (
    campaignActiveKey: string,
    noPermissionKey: string,
    defaultMessage: string = '',
  ): string => {
    if (campaignData?.status === CampaignRunStatusType.ACTIVE) {
      return t(campaignActiveKey);
    }
    if (currentUser?.role === UserRolesEnum.VIEWER) {
      return t(noPermissionKey);
    }
    return defaultMessage;
  };

  const handleSort = (field: SortingAgentColumns) => {
    const nextDirection: SortDirection =
      sortConfig.key === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';

    setSortConfig({ key: field, direction: nextDirection });
    setAgents((prevAgents) => sortAgentsByColumn(prevAgents, field, nextDirection, getSortValue));
  };

  const renderSortIcon = (field: SortingAgentColumns) => {
    if (sortConfig.key !== field) return null;

    return sortConfig.direction === 'asc' ? <IconArrowDown size="12" /> : <IconArrowUp size="12" />;
  };

  const handleDeleteBtn = (event: React.MouseEvent) => {
    event.stopPropagation();

    setShowDialog(true);

    if (selectedRowId) {
      setDeleteId(selectedRowId);
    }
    setAnchorEl(null);
  };

  const deleteAgent = async () => {
    if (deleteId) {
      setShowDialog(false);
      setLoading(true);
      setDeletingAgentId(deleteId);

      await new Promise((resolve) => {
        window.setTimeout(resolve, 260);
      });

      const updatedAgents = agents.filter((agent) => agent.id !== deleteId);
      setAgents(updatedAgents);
      setValue('agents', updatedAgents);

      const result = await agentOperations.deleteAgent(deleteId);

      if (result.newDefaultAgentId) {
        squadManagement.setDefaultAgentId(result.newDefaultAgentId);
      }
    }
    setDeleteId('');
    setDeletingAgentId(null);
    setLoading(false);
  };

  const handleMoreBtn = (event: any, rowId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);

    setSelectedRowId(rowId);
  };

  const handleEditAgent = (agent: AgentDto) => () => {
    setShowManageAgent('edit');
    setSelectedAgent(agent);
    handleClosePopover();
  };

  const handleMakeTestCall = (agent: AgentDto) => () => {
    setSelectedAgent(agent);
    handleClosePopover();
    setShowTestAgent(true);
  };

  const handleCloseTestAgentModal = () => {
    setShowTestAgent(false);
    setSelectedAgent(null);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleCopyAgentId = (agentId: string) => async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(agentId);
      setCopiedAgentId(agentId);
      setTimeout(() => {
        setCopiedAgentId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy agent ID:', error);
    }
  };

  const handleAdvancedSettings = (agent: AgentDto) => () => {
    setSelectedAgent(agent);
    setShowAdvancedSettings(true);
    handleClosePopover();
  };

  const handleAgentAmbienceSettings = (agent: AgentDto) => () => {
    setSelectedAgent(agent);
    setShowAgentAmbienceSettings(true);
    handleClosePopover();
  };

  const handleSetAsDefault = (agent: AgentDto) => async () => {
    handleClosePopover();
    setLoading(true);

    await squadManagement.setAsDefault(agent);

    setLoading(false);
  };

  const handleCloseAdvancedSettings = () => {
    setShowAdvancedSettings(false);
    setSelectedAgent(null);
  };

  const handleCloseAgentAmbienceSettings = () => {
    setShowAgentAmbienceSettings(false);
    setSelectedAgent(null);
  };

  const isDisabled = (additionalCondition?: boolean) => {
    const baseDisabled =
      currentUser?.role === UserRolesEnum.VIEWER ||
      campaignData?.status === CampaignRunStatusType.ACTIVE;
    return additionalCondition ? baseDisabled || additionalCondition : baseDisabled;
  };

  const isAdvancedSettingsDisabled = currentUser?.role === UserRolesEnum.VIEWER;
  const isAgentAmbienceReadOnly = isDisabled();
  const agentAmbienceReadOnlyReason =
    campaignData?.status === CampaignRunStatusType.ACTIVE
      ? t('adminMainPage.agentAmbienceSettings.readOnlyCampaign')
      : null;

  const getDisabledStyles = () => ({
    opacity: 0.5,
    '& svg': { opacity: 0.5 },
  });

  const handleCreateSquad = async (data: { name: string; description: string }) => {
    setLoading(true);
    await squadManagement.createSquad(data);
    setShowCreateSquadModal(false);
    setLoading(false);
  };

  const handleOpenCreateAgent = () => {
    setSelectedRowId(null);
    setSelectedAgent(null);
    setShowManageAgent('create');
  };

  const handleCloseManageAgent = async () => {
    setSelectedRowId(null);
    setShowManageAgent('');
    setSelectedAgent(null);
    await onRefresh();
  };

  const handleCloseCreateAgent = async () => {
    setSelectedRowId(null);
    setShowManageAgent('');
    setSelectedAgent(null);
    await onRefresh();
  };

  const handleCreateAgentSuccess = async () => {
    setSelectedRowId(null);
    setShowManageAgent('');
    setSelectedAgent(null);
    setPendingNewAgentAnimation(true);
    sessionStorage.setItem(newAgentAnimationStorageKey, String(Date.now()));

    if (onAgentCreated) {
      await onAgentCreated();
      return;
    }

    await onRefresh();
  };

  const renderRequiredHeader = (label: string) => (
    <>
      {label}
      <Typography component="span" className={classes.requiredMarker}>
        {t('adminMainPage.agents.requiredFieldMarker')}
      </Typography>
    </>
  );

  return (
    <>
      <Box className={classes.agent_header}>
        <Box display="flex" gap={1}>
          {/* Show based on campaign state */}
          {!hasSquad && agents.length === 0 && (
            // No agents yet - show both options
            <>
              <Tooltip
                title={
                  campaignData?.status === CampaignRunStatusType.ACTIVE
                    ? t('adminMainPage.squad.tooltips.cannotAddAgentCampaignActive')
                    : t('adminMainPage.squad.tooltips.addSingleAgent')
                }
                arrow>
                <span>
                  <Button
                    data-testid="agent-add-single"
                    color="primary"
                    variant="outlined"
                    size="small"
                    disabled={
                      currentUser?.role === UserRolesEnum.VIEWER ||
                      campaignData?.status === CampaignRunStatusType.ACTIVE
                    }
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateAgent}>
                    {t('adminMainPage.operation.addSingleAgent')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  campaignData?.status === CampaignRunStatusType.ACTIVE
                    ? t('adminMainPage.squad.tooltips.cannotAddAgentCampaignActive')
                    : t('adminMainPage.squad.tooltips.addSquad')
                }
                arrow>
                <span>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    disabled={
                      currentUser?.role === UserRolesEnum.VIEWER ||
                      campaignData?.status === CampaignRunStatusType.ACTIVE
                    }
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateSquadModal(true)}>
                    {t('adminMainPage.operation.addSquad')}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          {hasSingleAgent && (
            // Single agent exists - only show option to delete and recreate
            <Tooltip title={t('adminMainPage.squad.tooltips.removeExistingFirst')} arrow>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                disabled
                startIcon={<AddIcon />}>
                {t('adminMainPage.squad.labels.addAgentRemoveFirst')}
              </Button>
            </Tooltip>
          )}

          {hasSquad && (
            // Squad exists - show add agent button
            <Tooltip
              title={getActionTooltip(
                'adminMainPage.squad.tooltips.cannotAddAgentCampaignActive',
                'adminMainPage.squad.tooltips.noPermissionToAddAgents',
                t('adminMainPage.squad.tooltips.addAgentToSquad'),
              )}
              arrow>
              <span>
                <Button
                  data-testid="agent-add-to-squad"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={
                    currentUser?.role === UserRolesEnum.VIEWER ||
                    campaignData?.status === CampaignRunStatusType.ACTIVE
                  }
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateAgent}>
                  {t('adminMainPage.operation.addAgentToSquad')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      <TableContainer className={classes.agent_table}>
        <Table>
          <TableHead
            sx={{
              backgroundColor: theme.palette.extendedGrey?.lighter,
              height: '48px',
              borderTop: `1px solid ${theme.palette.divider}`,
            }}>
            <TableRow>
              <TableCell className={classes.agentIdCell}>
                <Typography
                  color={theme.palette.text.secondary}
                  className={classes.campagin_table_th}
                  variant="h6">
                  {t('adminMainPage.agents.agentId')}
                </Typography>
              </TableCell>
              <TableCell className={classes.startCell}>
                <Typography
                  color={theme.palette.text.secondary}
                  className={classes.campagin_table_th}
                  variant="h6">
                  {t('adminMainPage.operation.startTestCall')}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.nameCell}
                onClick={() => handleSort('mission.humanName')}>
                <Box className={classes.agent_table_sort_cell}>
                  <Typography
                    color={theme.palette.text.secondary}
                    variant="h6"
                    className={classNames(
                      classes.campagin_table_th,
                      classes.agent_table_first_cell,
                    )}>
                    {renderRequiredHeader(t('adminMainPage.agents.humanName'))}
                  </Typography>
                  {renderSortIcon('mission.humanName')}
                </Box>
              </TableCell>
              <TableCell className={classes.voiceCell}>
                <Stack spacing={0}>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('voice.gender')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {renderRequiredHeader(t('adminMainPage.agents.sex'))}
                    </Typography>
                    {renderSortIcon('voice.gender')}
                  </Box>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('voice.name')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {renderRequiredHeader(t('adminMainPage.agents.voice'))}
                    </Typography>
                    {renderSortIcon('voice.name')}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell
                className={classes.languageCell}
                onClick={() => handleSort('voice.accent')}>
                <Box className={classes.agent_table_sort_cell}>
                  <Typography
                    color={theme.palette.text.secondary}
                    className={classes.campagin_table_th}
                    variant="h6">
                    {renderRequiredHeader(t('adminMainPage.agents.language'))}
                  </Typography>
                  {renderSortIcon('voice.accent')}
                </Box>
              </TableCell>
              <TableCell className={classes.transcriberCell}>
                <Stack spacing={0}>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('transcriber.provider')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {renderRequiredHeader(t('adminMainPage.agents.transcriber'))}
                    </Typography>
                    {renderSortIcon('transcriber.provider')}
                  </Box>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('transcriber.language')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {t('adminMainPage.agents.language')}
                    </Typography>
                    {renderSortIcon('transcriber.language')}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell className={classes.llmCell}>
                <Stack spacing={0}>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('llm.provider')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {renderRequiredHeader(t('adminMainPage.agents.llmProvider'))}
                    </Typography>
                    {renderSortIcon('llm.provider')}
                  </Box>
                  <Box
                    className={classes.agent_table_sort_cell}
                    onClick={() => handleSort('llm.model')}>
                    <Typography
                      color={theme.palette.text.secondary}
                      className={classes.campagin_table_th}
                      variant="h6">
                      {renderRequiredHeader(t('adminMainPage.agents.llmModel'))}
                    </Typography>
                    {renderSortIcon('llm.model')}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell className={classes.profileCell} onClick={() => handleSort('profile')}>
                <Box className={classes.agent_table_sort_cell}>
                  <Typography
                    color={theme.palette.text.secondary}
                    className={classes.campagin_table_th}
                    variant="h6">
                    {renderRequiredHeader(t('adminMainPage.agents.profile'))}
                  </Typography>
                  {renderSortIcon('profile')}
                </Box>
              </TableCell>
              <TableCell className={classes.actionsCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents?.map((row) => {
              return selectedAgent?.id === row.id && showManageAgent == 'edit' ? (
                <TableRow
                  key={row.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  className={classes.agent_table_row}
                  sx={{ '& .MuiTableCell-root': { verticalAlign: 'top' } }}>
                  <ManageAgent
                    agents={agents}
                    agent={selectedAgent}
                    squadId={squadId}
                    transcriberData={transcriberData}
                    onClose={handleCloseManageAgent}
                  />
                </TableRow>
              ) : (
                <TableRow
                  className={classNames(classes.agent_table_row, {
                    [classes.agent_table_row_new]: newlyAddedAgentId === row.id,
                    [classes.agent_table_row_deleting]: deletingAgentId === row.id,
                  })}
                  key={row.id}
                  hover>
                  <TableCell className={classes.agentIdCell}>
                    <Box className={classes.agentIdContent}>
                      <Tooltip
                        title={
                          copiedAgentId === row.id
                            ? t('adminMainPage.operation.agentIdCopied')
                            : row.id
                        }
                        arrow
                        placement="top">
                        <IconButton
                          size="small"
                          onClick={handleCopyAgentId(row.id)}
                          className={classes.agentIdCopyButton}
                          aria-label={t('adminMainPage.operation.copyAgentId')}>
                          {copiedAgentId === row.id ? (
                            <CheckIcon sx={{ fontSize: '16px', color: 'success.main' }} />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: '16px' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.startCell}>
                    <Tooltip title={t('adminMainPage.operation.startTestCall')} arrow>
                      <IconButton
                        size="small"
                        className={classes.startButton}
                        onClick={handleMakeTestCall(row)}
                        aria-label={t('adminMainPage.operation.startTestCall')}>
                        <IconActionPlay />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="left"
                    className={classNames(classes.agent_table_first_cell, classes.nameCell)}>
                    <Box display="flex" padding={1} alignItems="center" gap={2}>
                      <AgentIcon width="24" height="24" />
                      <Tooltip title={row.name} arrow placement="top">
                        <Typography
                          variant="subtitle1"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '120px',
                          }}>
                          {row.name}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.voiceCell}>
                    <Box className={classes.voiceIconWithName} sx={{ minWidth: 0 }}>
                      <Box className={classes.genderIconContainer}>
                        {getVoiceGender(row).toLowerCase() === 'male' ? (
                          <Tooltip title="Male" arrow placement="top">
                            <MaleIcon className={classes.maleIcon} />
                          </Tooltip>
                        ) : getVoiceGender(row).toLowerCase() === 'female' ? (
                          <Tooltip title="Female" arrow placement="top">
                            <FemaleIcon className={classes.femaleIcon} />
                          </Tooltip>
                        ) : getVoiceGender(row) ? (
                          <Typography variant="subtitle1">{getVoiceGender(row)}</Typography>
                        ) : null}
                      </Box>
                      {getVoicePreviewUrl(row) ? (
                        <Tooltip
                          title={t('adminMainPage.agents.voicePreviewAvailable')}
                          arrow
                          placement="top">
                          <Box
                            component="button"
                            type="button"
                            aria-label={t('adminMainPage.agents.voicePreviewAvailable')}
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const previewUrl = getVoicePreviewUrl(row);
                              if (previewUrl) playVoicePreviewFromUserGesture(previewUrl);
                            }}
                            sx={{
                              border: 0,
                              padding: 0,
                              display: 'inline-flex',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: 'inherit',
                            }}>
                            <VolumeUpIcon className={classes.volumeUpIcon} color="primary" />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={t('adminMainPage.agents.noVoicePreview')}
                          arrow
                          placement="top">
                          <VolumeOffIcon className={classes.volumeOffIcon} color="disabled" />
                        </Tooltip>
                      )}
                      <Tooltip title={getVoiceName(row)} arrow placement="top">
                        <Typography variant="subtitle1" className={classes.voiceNameText}>
                          {getVoiceName(row)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.languageCell}>
                    <Typography variant="subtitle1">
                      {accentOptions.find((option) => option.key === row.languages?.[0])?.label ||
                        row.languages?.[0] ||
                        t('common.na')}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.transcriberCell}>
                    {(() => {
                      const transcriber = getTranscriber(row);
                      const label = transcriber
                        ? formatTranscriberLabel(transcriber)
                        : t('common.na');

                      return (
                        <Tooltip title={label} arrow placement="top">
                          <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{ minWidth: 0, maxWidth: '100%' }}>
                            {label}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={classes.llmCell}>
                    <LlmCompositeCell
                      row={row}
                      llmProviders={llmProviders}
                      providerModelsByProviderId={providerModelsByProviderId}
                    />
                  </TableCell>
                  <TableCell className={classes.profileCell}>
                    <Typography variant="subtitle1">
                      {(row.profile?.name as unknown as string) ||
                        DEFAULT_NO_ROLE_AGENT_PROFILE.name}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.actionsCell} align="center">
                    <Tooltip title="More actions">
                      <IconButton
                        disabled={currentUser?.role == UserRolesEnum.VIEWER}
                        aria-describedby={row.id.toString()}
                        onClick={(event) => handleMoreBtn(event, row.id)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  {selectedRowId === row.id && (
                    <Popover
                      id={row.id.toString()}
                      open={Boolean(anchorEl)}
                      anchorEl={anchorEl}
                      onClose={handleClosePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}>
                      <Box className={classes.agent_table_popover}>
                        <Tooltip
                          title={
                            campaignData?.status === CampaignRunStatusType.ACTIVE
                              ? t('adminMainPage.squad.tooltips.cannotEditAgentCampaignActive')
                              : ''
                          }
                          arrow
                          placement="left">
                          <span>
                            <IconButton
                              className={classes.agent_table_popover_button}
                              onClick={handleEditAgent(row)}
                              disabled={campaignData?.status === CampaignRunStatusType.ACTIVE}
                              sx={
                                campaignData?.status === CampaignRunStatusType.ACTIVE
                                  ? getDisabledStyles()
                                  : undefined
                              }>
                              <EditAgentIcon />
                              <Typography
                                sx={{
                                  color:
                                    campaignData?.status === CampaignRunStatusType.ACTIVE
                                      ? theme.palette.text.disabled
                                      : 'inherit',
                                }}>
                                {t('adminMainPage.operation.edit')}
                              </Typography>
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={''} arrow placement="left">
                          <span>
                            <IconButton
                              className={classes.agent_table_popover_button}
                              onClick={handleMakeTestCall(row)}
                              disabled={false}>
                              <CallIcon />
                              <Typography>{t('adminMainPage.operation.makeTestCall')}</Typography>
                            </IconButton>
                          </span>
                        </Tooltip>

                        <IconButton
                          className={classes.agent_table_popover_button}
                          onClick={handleAgentAmbienceSettings(row)}>
                          <VolumeUpIcon className={classes.volumeUpIcon} />
                          <Typography>{t('adminMainPage.operation.ambienceSettings')}</Typography>
                        </IconButton>

                        <Tooltip
                          title={getActionTooltip(
                            'adminMainPage.squad.tooltips.cannotModifySettingsCampaignActive',
                            'adminMainPage.squad.tooltips.noPermissionToModifySettings',
                          )}
                          arrow
                          placement="left">
                          <span>
                            <IconButton
                              className={classes.agent_table_popover_button}
                              onClick={handleAdvancedSettings(row)}
                              disabled={isAdvancedSettingsDisabled}
                              sx={isAdvancedSettingsDisabled ? getDisabledStyles() : undefined}>
                              <CampaignIcon />
                              <Typography
                                sx={{
                                  color: isAdvancedSettingsDisabled
                                    ? theme.palette.text.disabled
                                    : 'inherit',
                                }}>
                                {t('adminMainPage.operation.advancedSettings')}
                              </Typography>
                            </IconButton>
                          </span>
                        </Tooltip>

                        {isSquad && squadManagement.defaultAgentId !== row.id && (
                          <Tooltip
                            title={getActionTooltip(
                              'adminMainPage.squad.tooltips.cannotSetDefaultCampaignActive',
                              'adminMainPage.squad.tooltips.noPermissionToSetDefault',
                            )}
                            arrow
                            placement="left">
                            <span>
                              <IconButton
                                className={classes.agent_table_popover_button}
                                onClick={handleSetAsDefault(row)}
                                disabled={isDisabled()}
                                sx={{
                                  ...(isDisabled() ? { opacity: 0.5 } : {}),
                                  ...(!isDisabled() && {
                                    '&:hover svg': {
                                      transform: 'scale(1.2) rotate(-10deg)',
                                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    },
                                  }),
                                }}>
                                <StarBorderIcon
                                  sx={{
                                    color: isDisabled()
                                      ? theme.palette.text.disabled
                                      : theme.palette.warning.main,
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: isDisabled() ? theme.palette.text.disabled : 'inherit',
                                  }}>
                                  {t(SQUAD_OPERATIONS.SET_AS_DEFAULT)}
                                </Typography>
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        <Tooltip
                          title={
                            campaignData?.status === CampaignRunStatusType.ACTIVE
                              ? t('adminMainPage.squad.tooltips.cannotDeleteAgentCampaignActive')
                              : ''
                          }
                          arrow
                          placement="left">
                          <span>
                            <IconButton
                              className={classes.agent_table_popover_button}
                              onClick={handleDeleteBtn}
                              disabled={campaignData?.status === CampaignRunStatusType.ACTIVE}
                              sx={
                                campaignData?.status === CampaignRunStatusType.ACTIVE
                                  ? getDisabledStyles()
                                  : undefined
                              }>
                              <DeleteAgentIcon />
                              <Typography
                                color={
                                  campaignData?.status === CampaignRunStatusType.ACTIVE
                                    ? theme.palette.text.disabled
                                    : '#FA896B'
                                }>
                                {t('adminMainPage.operation.delete')}
                              </Typography>
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Popover>
                  )}
                </TableRow>
              );
            })}
            {showManageAgent === 'create' && (
              <TableRow sx={{ '& .MuiTableCell-root': { verticalAlign: 'top' } }}>
                <ManageAgent
                  agents={agents}
                  agent={null}
                  squadId={squadId}
                  transcriberData={transcriberData}
                  onClose={handleCloseCreateAgent}
                  onCreated={handleCreateAgentSuccess}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {formStateErrors.agents && showManageAgent === '' && !isSquad && (
        <Box sx={{ width: '100%', textAlign: 'center', p: '18px 16px' }}>
          <StyledErrorText sx={{ fontSize: '14px' }}>
            {t('adminMainPage.errors.atLeastOneAgent')}
          </StyledErrorText>
        </Box>
      )}
      {isSquad && agents.length === 0 && showManageAgent === '' && (
        <Box sx={{ width: '100%', textAlign: 'center', p: '18px 16px' }}>
          <StyledErrorText sx={{ fontSize: '14px' }}>
            {t(SQUAD_VALIDATION.AT_LEAST_ONE_AGENT)}
          </StyledErrorText>
        </Box>
      )}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          {t('adminMainPage.agents.deleteDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('adminMainPage.agents.deleteDialogContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setShowDialog(false)}>
            {t('adminMainPage.operation.cancel')}
          </Button>
          <Button onClick={deleteAgent} autoFocus>
            {t('adminMainPage.operation.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedAgent && (
        <>
          <TestingAgentPopup
            open={showTestAgent}
            onClose={handleCloseTestAgentModal}
            agentId={selectedAgent.id}
            agentName={selectedAgent.name}
            companyHash={currentUser?.companyId || ''}
            campaignId={campaignId || ''}
            squadId={squadId}
          />
          <AgentAdvancedSettingsModal
            open={showAdvancedSettings}
            onClose={handleCloseAdvancedSettings}
            agent={selectedAgent}
            agents={agents}
            campaignTools={(campaignData?.tools as ToolDto[]) || []}
            campaignName={campaignData?.title || ''}
            companyName={company?.name || ''}
            formMethods={agentToolsFormMethods}
            onSave={saveAgentTools}
            refetch={onRefresh}
            isReadOnly={
              campaignData?.status === CampaignRunStatusType.ACTIVE ||
              currentUser?.role === UserRolesEnum.VIEWER
            }
          />
          <AgentAmbienceSettingsModal
            open={showAgentAmbienceSettings}
            onClose={handleCloseAgentAmbienceSettings}
            agent={selectedAgent}
            campaignBackgroundSounds={campaignData?.backgroundSounds}
            isReadOnly={isAgentAmbienceReadOnly}
            readOnlyReason={agentAmbienceReadOnlyReason}
            onSaved={onRefresh}
          />
        </>
      )}

      <CreateSquadModal
        open={showCreateSquadModal}
        onClose={() => setShowCreateSquadModal(false)}
        onConfirm={handleCreateSquad}
      />
    </>
  );
};
